import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take, finalize } from 'rxjs/operators';
import { UsersService } from '../services/users.service';
import { environment } from '../../environments/environment';
import { AuthResult } from '../models/users/auth-result.model';


@Injectable({ providedIn: 'root' })
export class JwtInterceptor implements HttpInterceptor {
    private isRefreshing = false;
    private refreshingSubject = new BehaviorSubject<AuthResult>(null);

    constructor(
        private usersService: UsersService) { }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const currentUser = this.usersService.currentUser;
        const isLoggedIn = currentUser && currentUser.access_token;
        const isApiUrl = request.url.startsWith(environment.apiRootUrl);

        if (!isLoggedIn || !isApiUrl) {
            return next.handle(request);
        }

        const isTokenExpired = new Date() >= currentUser.expiration_date;

        request = this.addToken(request, currentUser);

        return next.handle(request)
            .pipe(
                catchError(error => {
                    if (error instanceof HttpErrorResponse && error.status === 401) {
                        return this.handle401Error(request, next);
                    } else if (error instanceof HttpErrorResponse && error.status === 500 && isTokenExpired) { // temporary decision while the API is not changed
                        return this.handle401Error(request, next);
                    } else {
                        return throwError(error);
                    }
                }));
    }

    private addToken(request: HttpRequest<any>, authResult: AuthResult): HttpRequest<any> {
        return request.clone({
            setHeaders: {
                Authorization: authResult.access_token
            }
        });
    }

    private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (!this.isRefreshing) {
            this.isRefreshing = true;
            this.refreshingSubject.next(null);

            return this.usersService.refreshToken()
                .pipe(
                    switchMap(authResult => {
                        this.refreshingSubject.next(authResult);
                        return next.handle(this.addToken(request, authResult));
                    }),
                    finalize(() => this.isRefreshing = false));

        } else {
            return this.refreshingSubject
                .pipe(
                    filter(authResult => authResult != null),
                    take(1),
                    switchMap(authResult => next.handle(this.addToken(request, authResult))));
        }
    }
}
